import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generate unique error ID for tracking
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.setState({
      error,
      errorInfo,
      errorId
    });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Global Error Boundary caught an error:', error.message);
    }

    // In production, you might want to send this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo, errorId);
    }
  }

  logErrorToService = (error, errorInfo, errorId) => {
    // Example: Send to error reporting service
    // You can integrate with services like Sentry, LogRocket, etc.
    try {
      const errorData = {
        errorId,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: this.getCurrentUserId()
      };

      // Send to your error reporting service
      const apiUrl = import.meta.env.VITE_API_URL || '';
      fetch(`${apiUrl}/api/errors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData)
      }).catch(err => {
        console.error('Failed to log error to service');
      });
    } catch (err) {
      console.error('Error in logErrorToService');
    }
  };

  getCurrentUserId = () => {
    try {
      // Try to get user ID from context or localStorage
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        return userData.userid || userData.id || 'unknown';
      }
    } catch (err) {
      console.error('Error getting user ID');
    }
    return 'unknown';
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex justify-center items-center p-4 min-h-screen bg-gradient-to-br from-red-50 to-red-100">
          <div className="p-6 w-full max-w-md text-center bg-white rounded-lg shadow-lg">
            <div className="mb-4">
              <AlertTriangle className="mx-auto mb-4 w-16 h-16 text-red-500" />
              <h1 className="mb-2 text-2xl font-bold text-gray-900">
                Oops! Something went wrong
              </h1>
              <p className="mb-4 text-gray-600">
                We're sorry, but something unexpected happened. Our team has been notified.
              </p>
            </div>

            {this.state.errorId && (
              <div className="p-3 mb-4 bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-600">
                  Error ID: <code className="font-mono text-xs">{this.state.errorId}</code>
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="flex gap-2 justify-center items-center px-4 py-2 w-full text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              
              <button
                onClick={this.handleReload}
                className="flex gap-2 justify-center items-center px-4 py-2 w-full text-white bg-gray-600 rounded-lg transition-colors hover:bg-gray-700"
              >
                <RefreshCw className="w-4 h-4" />
                Reload Page
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="flex gap-2 justify-center items-center px-4 py-2 w-full text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700"
              >
                <Home className="w-4 h-4" />
                Go Home
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">
                  Error Details (Development Only)
                </summary>
                <div className="p-3 mt-2 bg-red-50 rounded-lg">
                  <pre className="overflow-auto max-h-40 text-xs text-red-800 whitespace-pre-wrap">
                    {this.state.error.toString()}
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
